import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import placeholderImage from '../../../assets/images/noimage.png';
import { deleteUserAdById } from '../../../api/deleteUserAd';
import { activateAdById, deactivateAdById } from '../../../api/statusChangeUserAd';
import { getRejectMessage } from '../../../api/get/getRejectMessage';
import { getPromotionInfo } from '../../../api/getPromotionInfo';
import { toggleAutoRenewal } from '../../../api/toggleAutoRenewal';
import { formatPrice } from '../../../utils/formatPrice';
import { t } from 'i18next';
import ToggleSwitch from '../../elements/ToggleSwitch/ToggleSwitch';

const STATUS_CONFIG = {
    active:   { label: () => t('activeStatus')   || 'Белсенді',     bg: 'bg-[#147F26]/10', text: 'text-[#147F26]',  dot: 'bg-[#147F26]' },
    pending:  { label: () => t('pendingStatus')  || 'Модерацияда',  bg: 'bg-yellow-50',    text: 'text-yellow-600', dot: 'bg-yellow-400' },
    inactive: { label: () => t('inactiveStatus') || 'Белсенді емес',bg: 'bg-gray-100',     text: 'text-gray-500',   dot: 'bg-gray-400' },
    rejected: { label: () => t('rejectedStatus') || 'Қабылданбады', bg: 'bg-red-50',       text: 'text-red-500',    dot: 'bg-red-500' },
};

const ProfileAdvertisementsItem = ({ item }) => {
    const { main_image_url, title, price, id, calls_count, views_count, favorites_count, messages_count, created_at, status, auto_renewal } = item;
    const navigate = useNavigate();
    const [isAutoRenewal, setIsAutoRenewal] = useState(auto_renewal || false);
    const [promotionInfo, setPromotionInfo] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        if (status !== 'active') { setPromotionInfo(null); return; }
        getPromotionInfo(id).then(res => {
            const info = res?.promotion || res;
            if (info?.status === 'active') {
                setPromotionInfo(info);
                if (info.auto_renewal !== undefined) setIsAutoRenewal(info.auto_renewal);
            } else setPromotionInfo(null);
        }).catch(() => setPromotionInfo(null));
    }, [id, status]);

    const handleAdClick = () => navigate(`/adshow/${id}`);

    const handleAction = async (e, fn) => {
        e.stopPropagation();
        setActionLoading(true);
        try { await fn(); window.location.reload(); }
        catch (err) { alert(err.message); }
        finally { setActionLoading(false); }
    };

    const handleRejectMessage = async (e) => {
        e.stopPropagation();
        try {
            const res = await getRejectMessage(id);
            alert(`${t('rejectReason') || 'Бас тарту себебі'}: ${res.message || t('reasonNotSpecified') || 'Көрсетілмеген'}`);
        } catch { alert(t('failedToGetReason') || 'Себепті алу мүмкін болмады'); }
    };

    const handleAutoRenewalToggle = async (value) => {
        if (!promotionInfo?.id) return;
        const prev = isAutoRenewal;
        setIsAutoRenewal(value);
        try {
            const res = await toggleAutoRenewal(promotionInfo.id, value);
            setPromotionInfo({ ...promotionInfo, auto_renewal: res.auto_renewal });
        } catch { setIsAutoRenewal(prev); }
    };

    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.inactive;

    return (
        <div
            className="flex gap-4 bg-white rounded-2xl p-4 cursor-pointer hover:shadow-md transition-shadow border border-gray-100"
            onClick={handleAdClick}
        >
            {/* Image */}
            <div className="flex-shrink-0 w-28 h-28 rounded-xl overflow-hidden bg-gray-100">
                <img src={main_image_url || placeholderImage} alt={title} className="w-full h-full object-cover" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 flex flex-col justify-between">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{title}</p>
                        <p className="text-base font-bold text-[#147F26] mt-0.5">
                            {formatPrice(price, item.price_type, item.is_negotiable, item.barter)}
                        </p>
                    </div>
                    {/* Status badge */}
                    <span className={`flex-shrink-0 flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-lg ${cfg.bg} ${cfg.text}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
                        {cfg.label()}
                    </span>
                </div>

                {/* Stats */}
                <div className="flex items-center gap-3 mt-2">
                    {[
                        { icon: "M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z", val: views_count },
                        { icon: "M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z", val: calls_count },
                        { icon: "M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z", val: favorites_count },
                        { icon: "M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z", val: messages_count },
                    ].map((s, i) => (
                        <span key={i} className="flex items-center gap-1 text-xs text-gray-400">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d={s.icon} />
                            </svg>
                            {s.val || 0}
                        </span>
                    ))}
                    <span className="text-xs text-gray-300 ml-auto">
                        {new Date(created_at).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-1 mt-2 flex-wrap" onClick={e => e.stopPropagation()}>
                    {/* Status action */}
                    {status === 'active' && (
                        <button
                            disabled={actionLoading}
                            onClick={e => handleAction(e, () => deactivateAdById(id))}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-500 hover:bg-red-50 transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                            </svg>
                            {t('deactivate')}
                        </button>
                    )}
                    {status === 'inactive' && (
                        <button
                            disabled={actionLoading}
                            onClick={e => handleAction(e, () => activateAdById(id))}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-[#147F26] hover:bg-[#147F26]/5 transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            {t('activate')}
                        </button>
                    )}
                    {status === 'rejected' && (
                        <button
                            onClick={handleRejectMessage}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-yellow-600 hover:bg-yellow-50 transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {t('causeRejectAd')}
                        </button>
                    )}

                    {/* Edit */}
                    {status !== 'rejected' && (
                        <button
                            onClick={e => { e.stopPropagation(); navigate('/edit', { state: { adId: id } }); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-gray-500 hover:bg-gray-100 transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            {t('edit')}
                        </button>
                    )}

                    {/* Promote */}
                    {status === 'active' && (
                        <button
                            onClick={e => { e.stopPropagation(); navigate('/promote', { state: { ad: item } }); }}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-[#147F26] bg-[#147F26]/5 hover:bg-[#147F26]/10 transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                            </svg>
                            {t('promote')}
                        </button>
                    )}

                    {/* Delete */}
                    {(status === 'inactive' || status === 'rejected') && (
                        <button
                            disabled={actionLoading}
                            onClick={e => handleAction(e, () => deleteUserAdById(id))}
                            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium text-red-400 hover:bg-red-50 transition-colors"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {t('delete')}
                        </button>
                    )}

                    {/* Auto renewal */}
                    {status === 'active' && promotionInfo?.status === 'active' && (
                        <div className="flex items-center gap-2 ml-auto">
                            <span className="text-xs text-gray-500">{t('autoRenewal')}</span>
                            <ToggleSwitch initialValue={isAutoRenewal} onToggle={handleAutoRenewalToggle} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfileAdvertisementsItem;
